import { Pipe, PipeTransform, inject } from '@angular/core';
import { ApiConfiguration } from '../../api/api-configuration';

export interface FileUrlOptions {
  /** Optional cache-busting or revalidation parameter appended as ?v=... */
  v?: string | number | Date;
  /** Optional fallback src when fileId missing */
  fallback?: string;
}

/**
 * Admin Image Src Pipe (Dashboard version - backward compatible)
 * Generates public file URLs from file IDs using the backend files endpoint
 * Endpoint: /api/v1/files/file/:id
 *
 * Usage:
 *   {{ fileId | adminImageSrc }}
 *   {{ fileId | adminImageSrc : projectId : { v: timestamp } }}
 *
 * Note: projectId parameter is now ignored (kept for backward compatibility)
 */
@Pipe({
  name: 'adminImageSrc',
  standalone: true,
})
export class AdminImageSrcPipe implements PipeTransform {
  private readonly apiCfg = inject(ApiConfiguration);

  /**
   * @param fileId - File ID
   * @param projectIdOrOpts - (backward compat) Can be projectId (string, ignored) or options object
   * @param opts - Options (used if projectIdOrOpts is string)
   */
  transform(
    fileId?: string | null,
    projectIdOrOpts?: string | null | FileUrlOptions,
    opts?: FileUrlOptions
  ): string {
    if (!fileId) {
      const options = typeof projectIdOrOpts === 'object' ? projectIdOrOpts : opts;
      return options?.fallback ?? '';
    }

    const base = (this.apiCfg?.rootUrl || '/api').replace(/\/$/, '');
    let url = `${base}/v1/files/file/${encodeURIComponent(fileId)}`;

    // Handle backward compatibility: if second param is string (projectId), use third param as opts
    const options = typeof projectIdOrOpts === 'object' ? projectIdOrOpts : opts;

    if (options?.v !== undefined && options?.v !== null) {
      const vVal = options.v instanceof Date ? options.v.getTime() : String(options.v);
      url += `?v=${encodeURIComponent(vVal)}`;
    }

    return url;
  }
}
